import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { getBackend } from "@/lib/backend/backend2";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import type { ICategory, ICategoryItem } from "@/lib/backend/manufacturing2.generated.types";
import Select from "react-select";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import type { ILabelValuePair } from "@/lib/util";

import "./LinesAddCategoryItemModal.scss";

type Props = {
    show: boolean;
    onHide: () => void;
    onSave: (category: ICategory, category_item: ICategoryItem) => Promise<void>;
    error: string | null;
    isLoading: boolean;
    line_categories: ICategory[];
};

/**
 * Modal to create and edit categories for lines
 */
const LinesAddCategoryItemModal = (props: Props) => {
    const { show, onHide, onSave, error, isLoading, line_categories } = props;
    const [categories, setCategories] = useState<ICategory[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<ILabelValuePair<number> | null>(null);
    const [selectedCategoryItem, setSelectedCategoryItem] = useState<ILabelValuePair<number | null> | null>(null);

    useEffect(() => {
        if (show) {
            loadCategories();
        }
    }, [show, line_categories]);

    const loadCategories = useCallback(async () => {
        try {
            const res = await getBackend().manufacturing.getCategories({ include_items: true });

            // Create a Set of existing category item IDs for faster lookup
            const existing_item_ids = new Set<number | null>();
            for (const category of line_categories) {
                for (const item of category.items || []) {
                    existing_item_ids.add(item.id);
                }
            }

            // Filter out items that already exist in line_categories
            const filtered_categories = res.categories
                .map(category => {
                    const available_items = category.items.filter(item => !existing_item_ids.has(item.id));
                    return { ...category, items: available_items };
                })
                .filter(category => category.items.length > 0);

            setCategories(filtered_categories);
        } catch (error) {
            console.error("Failed to load categories:", error);
            setCategories([]);
        }
    }, [line_categories]);

    const handleSelectCategory = useCallback((category: ILabelValuePair<number>) => {
        setSelectedCategory(category);
        setSelectedCategoryItem(null);
    }, []);

    const handleSelectCategoryItem = useCallback((category_item: ILabelValuePair<number | null>) => {
        setSelectedCategoryItem(category_item);
    }, []);

    const category_options = useMemo(() => {
        return categories.map((category: ICategory) => ({
            value: category.id,
            label: category.name,
        }));
    }, [categories]);

    const category_item_options = useMemo(() => {
        const selected_cat = categories.find(category => category.id === selectedCategory?.value);
        const items = selected_cat?.items || [];
        return items.map(item => ({
            value: item.id,
            label: item.name,
        }));
    }, [selectedCategory, categories]);

    const handleSave = useCallback(async () => {
        if (!selectedCategory || !selectedCategoryItem) {
            return;
        }

        const category = categories.find(cat => cat.id === selectedCategory.value);
        const category_item = category?.items.find(item => item.id === selectedCategoryItem.value);

        if (!category || !category_item) {
            return;
        }

        try {
            handleClearCategory();
            await onSave(category, category_item);
        } catch (error) {
            console.error("Failed to save category:", error);
        }
    }, [selectedCategory, selectedCategoryItem, categories, onSave]);

    const handleClearCategory = useCallback(() => {
        setSelectedCategory(null);
        setSelectedCategoryItem(null);
    }, []);

    const handleCloseModal = useCallback(() => {
        handleClearCategory();
        onHide();
    }, [onHide]);

    return (
        <Modal className="lines-add-category-item-modal" show={show} onHide={handleCloseModal}>
            <Modal.Header>
                <Modal.Title>{translate("Categories.create_category_item", "Add category item")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <ErrorComponent msg={error} type="error" className="mt-0" />}
                <div className="form-group">
                    <label htmlFor="categorySelect">{translate("common.category", "Category")}</label>
                    <Select
                        inputId="categorySelect"
                        options={category_options}
                        value={selectedCategory}
                        onChange={handleSelectCategory}
                        isLoading={isLoading}
                        isDisabled={isLoading}
                        autoFocus
                    />
                </div>
                {selectedCategory && (
                    <div className="form-group">
                        <label htmlFor="categoryItemSelect">{translate("common.category_item", "Category item")}</label>
                        <Select
                            inputId="categoryItemSelect"
                            options={category_item_options}
                            value={selectedCategoryItem}
                            onChange={handleSelectCategoryItem}
                            isLoading={isLoading}
                            isDisabled={isLoading}
                            autoFocus
                        />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-outline-secondary" onClick={handleCloseModal}>
                    {translate("common.cancel", "Cancel")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isLoading || !selectedCategory || !selectedCategoryItem}
                >
                    {translate("common.save", "Save")}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default LinesAddCategoryItemModal;
